import { buildHyperlinkHref } from './bq-hyperlink-columns';
import {
  INTERVIEW_SCHEDULER_ROLE_OPTIONS,
  type InterviewSchedulerRoleKey,
} from './submission-choice.models';

/** Persisted assistant metadata when ATS interview scheduling completes (`metadata.type`). */
export const INTERVIEW_SCHEDULE_RESULT_META_TYPE = 'interview_schedule_result';

export const INTERVIEW_SCHEDULING_AGENT_TOOL_NAME = 'interview_scheduling_agent';

export interface InterviewScheduleResultRowPreview {
  candidateName: string;
  candidateEmail: string;
  candidateId: string;
  jobTitle: string;
  jobCode: string;
  jobCompany: string;
  submissionId: string;
  submissionCode: string;
  jobId: string;
  message: string;
  scheduledBy: string;
  scheduledByLabel: string;
  applicantEncryptedId: string;
  jobEncryptedId: string;
  submissionEncryptedId: string;
  applicantHref: string | null;
  jobHref: string | null;
  submissionHref: string | null;
}

export interface InterviewScheduleResultPreviewModel {
  message: string;
  summary: string;
  responseMessage: string;
  successSchedules: InterviewScheduleResultRowPreview[];
  failedSchedules: InterviewScheduleResultRowPreview[];
  skippedSchedules: InterviewScheduleResultRowPreview[];
  processedCount: number;
  successCount: number;
  skippedCount: number;
  failedCount: number;
}

export interface InterviewScheduleResultDisplayCopy {
  message: string;
  summary: string;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asIdString(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  return '';
}

function readScheduleRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (item): item is Record<string, unknown> =>
      item != null && typeof item === 'object' && !Array.isArray(item),
  );
}

function firstNonEmpty(...values: unknown[]): string {
  for (const v of values) {
    const s = asTrimmedString(v) || asIdString(v);
    if (s) {
      return s;
    }
  }
  return '';
}

function scheduledByLabelFromKey(key: string): string {
  const hit = INTERVIEW_SCHEDULER_ROLE_OPTIONS.find((o) => o.key === key);
  return hit?.label ?? key;
}

function buildRowPreview(
  row: Record<string, unknown>,
  atsThDomain: string | null | undefined,
): InterviewScheduleResultRowPreview {
  const candidateName = firstNonEmpty(row['candidate_name'], row['applicant_name']);
  const candidateEmail = firstNonEmpty(row['candidate_email'], row['email'], row['applicant_email']);
  const candidateId = firstNonEmpty(row['candidate_id'], row['applicant_id']);
  const jobTitle = asTrimmedString(row['job_title']);
  const jobCode = firstNonEmpty(row['job_code'], row['job_id']);
  const jobCompany = asTrimmedString(row['job_company']);
  const submissionId = firstNonEmpty(row['submission_id']);
  // Prefer display code (`submission_code` / `submission_display_id`) over row PK.
  const submissionCode = firstNonEmpty(
    row['submission_code'],
    row['submissionCode'],
    row['submission_display_id'],
    row['submissionDisplayId'],
  );
  const jobId = firstNonEmpty(row['job_id'], jobCode);
  const scheduledBy = firstNonEmpty(row['scheduled_by'], row['scheduledBy']);
  const scheduledByLabel =
    firstNonEmpty(row['scheduled_by_label'], row['scheduledByLabel']) ||
    (scheduledBy ? scheduledByLabelFromKey(scheduledBy) : '');
  const applicantEnc = firstNonEmpty(
    row['applicant_encrypted_id'],
    row['applicantEncryptedID'],
    row['applicantEncryptedId'],
  );
  const jobEnc = firstNonEmpty(
    row['job_encrypted_id'],
    row['jobEncryptedID'],
    row['jobEncryptedId'],
  );
  const submissionEnc = firstNonEmpty(
    row['submission_encrypted_id'],
    row['submissionEncryptedID'],
    row['submissionEncryptedId'],
  );
  return {
    candidateName,
    candidateEmail,
    candidateId,
    jobTitle,
    jobCode,
    jobCompany,
    submissionId,
    submissionCode,
    jobId,
    message: asTrimmedString(row['message']),
    scheduledBy,
    scheduledByLabel,
    applicantEncryptedId: applicantEnc,
    jobEncryptedId: jobEnc,
    submissionEncryptedId: submissionEnc,
    applicantHref: applicantEnc
      ? buildHyperlinkHref(atsThDomain, 'applicants', applicantEnc)
      : null,
    jobHref: jobEnc ? buildHyperlinkHref(atsThDomain, 'jobs', jobEnc) : null,
    submissionHref: submissionEnc
      ? buildHyperlinkHref(atsThDomain, 'submissions', submissionEnc)
      : null,
  };
}

/** Copy encrypted ids / hrefs onto sparse failed/skipped rows from any sibling with the same submission id. */
function backfillRowLinksFromSiblings(
  rows: InterviewScheduleResultRowPreview[],
  donors: InterviewScheduleResultRowPreview[],
): InterviewScheduleResultRowPreview[] {
  const normalizeId = (id: string): string => {
    const t = id.trim();
    if (!t) {
      return '';
    }
    const n = Number.parseInt(t, 10);
    return Number.isFinite(n) && n > 0 ? String(n) : t;
  };
  const byId = new Map<string, InterviewScheduleResultRowPreview>();
  for (const d of donors) {
    const id = normalizeId(d.submissionId || d.submissionCode || '');
    if (!id) {
      continue;
    }
    const prev = byId.get(id);
    if (!prev) {
      byId.set(id, d);
      continue;
    }
    byId.set(id, {
      ...prev,
      applicantEncryptedId: prev.applicantEncryptedId || d.applicantEncryptedId,
      jobEncryptedId: prev.jobEncryptedId || d.jobEncryptedId,
      submissionEncryptedId: prev.submissionEncryptedId || d.submissionEncryptedId,
      applicantHref: prev.applicantHref || d.applicantHref,
      jobHref: prev.jobHref || d.jobHref,
      submissionHref: prev.submissionHref || d.submissionHref,
      candidateName: prev.candidateName || d.candidateName,
      candidateEmail: prev.candidateEmail || d.candidateEmail,
      jobCode: prev.jobCode || d.jobCode,
      jobTitle: prev.jobTitle || d.jobTitle,
    });
  }
  return rows.map((row) => {
    const id = normalizeId(row.submissionId || row.submissionCode || '');
    const donor = id ? byId.get(id) : undefined;
    if (!donor) {
      return row;
    }
    return {
      ...row,
      applicantEncryptedId: row.applicantEncryptedId || donor.applicantEncryptedId,
      jobEncryptedId: row.jobEncryptedId || donor.jobEncryptedId,
      submissionEncryptedId: row.submissionEncryptedId || donor.submissionEncryptedId,
      applicantHref: row.applicantHref || donor.applicantHref,
      jobHref: row.jobHref || donor.jobHref,
      submissionHref: row.submissionHref || donor.submissionHref,
      candidateName: row.candidateName || donor.candidateName,
      candidateEmail: row.candidateEmail || donor.candidateEmail,
      jobCode: row.jobCode || donor.jobCode,
      jobTitle: row.jobTitle || donor.jobTitle,
    };
  });
}

export function buildInterviewScheduleBatchCopyFromCounts(
  successCount: number,
  failedCount: number,
  skippedCount = 0,
): InterviewScheduleResultDisplayCopy {
  const ok = Math.max(0, Math.floor(successCount));
  const failed = Math.max(0, Math.floor(failedCount));
  const skipped = Math.max(0, Math.floor(skippedCount));
  const total = ok + failed + skipped;
  const parts = [`${ok} scheduled`];
  if (skipped > 0) {
    parts.push(`${skipped} skipped`);
  }
  if (failed > 0 || skipped === 0) {
    parts.push(`${failed} failed`);
  }
  const summary = `Processed ${total} interview${total === 1 ? '' : 's'}: ${parts.join(', ')}.`;
  if (ok > 0 && failed === 0 && skipped === 0) {
    return { message: 'Interview invitation sent successfully.', summary };
  }
  if (ok === 0 && failed > 0 && skipped === 0) {
    return { message: 'Interview scheduling failed.', summary };
  }
  if (ok === 0 && failed === 0 && skipped > 0) {
    return { message: 'Interview scheduling skipped.', summary };
  }
  if (ok > 0 && (failed > 0 || skipped > 0)) {
    return { message: 'Interviews scheduled with some exceptions.', summary };
  }
  if (failed > 0 && skipped > 0 && ok === 0) {
    return { message: 'Interview scheduling completed with exceptions.', summary };
  }
  return { message: 'No interview scheduling results.', summary };
}

function resolveInterviewCopy(
  metadata: Record<string, unknown>,
  successCount: number,
  failedCount: number,
  skippedCount: number,
): InterviewScheduleResultDisplayCopy {
  const message =
    asTrimmedString(metadata['message']) ||
    asTrimmedString(metadata['responseMessage']);
  const summary = asTrimmedString(metadata['summary']);
  if (message && summary) {
    return { message, summary };
  }
  const fallback = buildInterviewScheduleBatchCopyFromCounts(
    successCount,
    failedCount,
    skippedCount,
  );
  return {
    message: message || fallback.message,
    summary: summary || fallback.summary,
  };
}

function toPreviewModel(
  metadata: Record<string, unknown>,
  successSchedules: InterviewScheduleResultRowPreview[],
  failedSchedules: InterviewScheduleResultRowPreview[],
  skippedSchedules: InterviewScheduleResultRowPreview[],
  extraDonors: InterviewScheduleResultRowPreview[] = [],
): InterviewScheduleResultPreviewModel {
  const donors = [...successSchedules, ...failedSchedules, ...skippedSchedules, ...extraDonors];
  const success = backfillRowLinksFromSiblings(successSchedules, donors);
  const failed = backfillRowLinksFromSiblings(failedSchedules, donors);
  const skipped = backfillRowLinksFromSiblings(skippedSchedules, donors);
  const successCount = success.length;
  const failedCount = failed.length;
  const skippedCount = skipped.length;
  const copy = resolveInterviewCopy(metadata, successCount, failedCount, skippedCount);
  return {
    message: copy.message,
    summary: copy.summary,
    responseMessage: copy.message,
    successSchedules: success,
    failedSchedules: failed,
    skippedSchedules: skipped,
    processedCount: successCount + failedCount + skippedCount,
    successCount,
    skippedCount,
    failedCount,
  };
}

function readSkippedRows(
  metadata: Record<string, unknown>,
  atsThDomain: string | null | undefined,
): InterviewScheduleResultRowPreview[] {
  return readScheduleRows(
    metadata['skipped_interview_schedules'] ?? metadata['skipped_schedules'],
  ).map((row) => buildRowPreview(row, atsThDomain));
}

/**
 * Submission detail rows (new/existing) used to backfill encrypted portal ids onto
 * sparse ATS failed/success interview rows when reopening an older task graph.
 */
export function collectSubmissionDetailDonorRowsFromSources(
  sources: Array<Record<string, unknown> | null | undefined>,
): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  const pushRow = (row: Record<string, unknown>) => {
    const sid = firstNonEmpty(row['submission_id'], row['submissionId']);
    const key = sid || JSON.stringify(row);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(row);
  };
  const pushFromPayload = (rec: Record<string, unknown>) => {
    for (const key of ['new_submissions', 'existing_submissions'] as const) {
      const arr = rec[key];
      if (!Array.isArray(arr)) {
        continue;
      }
      for (const item of arr) {
        if (item != null && typeof item === 'object' && !Array.isArray(item)) {
          pushRow(item as Record<string, unknown>);
        }
      }
    }
    if (Array.isArray(rec['rows'])) {
      for (const item of rec['rows']) {
        if (item != null && typeof item === 'object' && !Array.isArray(item)) {
          const row = item as Record<string, unknown>;
          if (
            firstNonEmpty(
              row['applicant_encrypted_id'],
              row['job_encrypted_id'],
              row['submission_encrypted_id'],
              row['applicantEncryptedID'],
              row['jobEncryptedID'],
              row['submissionEncryptedID'],
            )
          ) {
            pushRow(row);
          }
        }
      }
    }
  };

  for (const source of sources) {
    if (!source) {
      continue;
    }
    pushFromPayload(source);
    const nested = source['result'];
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
      pushFromPayload(nested as Record<string, unknown>);
    }
    const ctx = source['context'];
    if (ctx != null && typeof ctx === 'object' && !Array.isArray(ctx)) {
      pushFromPayload(ctx as Record<string, unknown>);
    }
    const input = source['input'];
    if (input != null && typeof input === 'object' && !Array.isArray(input)) {
      const inputRec = input as Record<string, unknown>;
      pushFromPayload(inputRec);
      const inputCtx = inputRec['context'];
      if (inputCtx != null && typeof inputCtx === 'object' && !Array.isArray(inputCtx)) {
        pushFromPayload(inputCtx as Record<string, unknown>);
      }
    }
    const output = source['output'];
    if (output != null && typeof output === 'object' && !Array.isArray(output)) {
      pushFromPayload(output as Record<string, unknown>);
    }
  }
  return out;
}

/** Pull donor rows from a persisted task graph (sibling submission task + interview input.context). */
export function collectSubmissionDetailDonorRowsFromTaskGraph(
  taskGraph: { tasks?: Record<string, unknown> } | null | undefined,
  interviewTaskId?: string | null,
): Record<string, unknown>[] {
  if (!taskGraph?.tasks || typeof taskGraph.tasks !== 'object') {
    return [];
  }
  const sources: Array<Record<string, unknown> | null | undefined> = [];
  for (const [id, raw] of Object.entries(taskGraph.tasks)) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      continue;
    }
    const task = raw as Record<string, unknown>;
    const tool = String(task['toolName'] ?? task['name'] ?? '')
      .trim()
      .toLowerCase();
    if (tool === 'submission_agent' || tool === 'pipeline_agent') {
      sources.push(task);
      if (task['output'] != null && typeof task['output'] === 'object') {
        sources.push(task['output'] as Record<string, unknown>);
      }
    }
    if (interviewTaskId && id === interviewTaskId) {
      sources.push(task);
    }
  }
  return collectSubmissionDetailDonorRowsFromSources(sources);
}

export function enrichInterviewSchedulePreviewWithSubmissionDonors(
  preview: InterviewScheduleResultPreviewModel,
  donorRows: Record<string, unknown>[],
  atsThDomain?: string | null,
): InterviewScheduleResultPreviewModel {
  if (!donorRows.length) {
    return preview;
  }
  const donors = donorRows.map((row) => buildRowPreview(row, atsThDomain));
  return {
    ...preview,
    successSchedules: backfillRowLinksFromSiblings(preview.successSchedules, donors),
    failedSchedules: backfillRowLinksFromSiblings(preview.failedSchedules, donors),
    skippedSchedules: backfillRowLinksFromSiblings(preview.skippedSchedules, donors),
  };
}

/** Builds right-panel preview from assistant metadata or task `output`. */
export function parseInterviewScheduleResultPreviewFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
  atsThDomain?: string | null,
  submissionDonorRows?: Record<string, unknown>[],
): InterviewScheduleResultPreviewModel | null {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  const type = asTrimmedString(metadata['type']);
  const resultKind = asTrimmedString(metadata['resultKind']);
  const isInterviewMeta =
    type === INTERVIEW_SCHEDULE_RESULT_META_TYPE ||
    type === 'interview_schedule_action' ||
    resultKind === INTERVIEW_SCHEDULE_RESULT_META_TYPE ||
    resultKind === 'interview_schedule_action';
  if (!isInterviewMeta) {
    const hasArrays =
      Array.isArray(metadata['success_interview_schedules']) ||
      Array.isArray(metadata['failed_interview_schedules']) ||
      Array.isArray(metadata['skipped_interview_schedules']);
    if (!hasArrays) {
      return null;
    }
  }

  const successSchedules = readScheduleRows(metadata['success_interview_schedules']).map((row) =>
    buildRowPreview(row, atsThDomain),
  );
  const failedSchedules = readScheduleRows(metadata['failed_interview_schedules']).map((row) =>
    buildRowPreview(row, atsThDomain),
  );
  const skippedSchedules = readSkippedRows(metadata, atsThDomain);
  const extraDonors = (submissionDonorRows ?? []).map((row) => buildRowPreview(row, atsThDomain));

  if (
    successSchedules.length === 0 &&
    failedSchedules.length === 0 &&
    skippedSchedules.length === 0
  ) {
    const nested = metadata['result'];
    if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
      const n = nested as Record<string, unknown>;
      const nestedSuccess = readScheduleRows(n['success_interview_schedules']).map((row) =>
        buildRowPreview(row, atsThDomain),
      );
      const nestedFailed = readScheduleRows(n['failed_interview_schedules']).map((row) =>
        buildRowPreview(row, atsThDomain),
      );
      const nestedSkipped = readSkippedRows(n, atsThDomain);
      if (nestedSuccess.length > 0 || nestedFailed.length > 0 || nestedSkipped.length > 0) {
        return toPreviewModel(
          { ...n, ...metadata },
          nestedSuccess,
          nestedFailed,
          nestedSkipped,
          extraDonors,
        );
      }
    }
    return null;
  }
  return toPreviewModel(metadata, successSchedules, failedSchedules, skippedSchedules, extraDonors);
}

export function buildInterviewScheduleResultMetadataFromOutput(
  output: unknown,
  options?: { taskId?: string; taskGraphId?: string },
): Record<string, unknown> | null {
  if (output == null || typeof output !== 'object' || Array.isArray(output)) {
    return null;
  }
  const o = output as Record<string, unknown>;
  if (asTrimmedString(o['type']) !== 'interview_schedule_action') {
    return null;
  }
  const preview = parseInterviewScheduleResultPreviewFromMetadata(o);
  if (!preview) {
    return null;
  }
  const taskId = options?.taskId?.trim() ?? '';
  const taskGraphId = options?.taskGraphId?.trim() ?? '';
  return {
    type: INTERVIEW_SCHEDULE_RESULT_META_TYPE,
    resultKind: INTERVIEW_SCHEDULE_RESULT_META_TYPE,
    message: preview.message,
    summary: preview.summary,
    responseMessage: preview.message,
    ats_message: preview.message,
    success_interview_schedules: o['success_interview_schedules'],
    failed_interview_schedules: o['failed_interview_schedules'],
    skipped_interview_schedules: o['skipped_interview_schedules'],
    ...(taskId ? { taskId } : {}),
    ...(taskGraphId ? { taskGraphId } : {}),
  };
}

export function resolveInterviewScheduleResultDisplay(
  metadata: Record<string, unknown> | null | undefined,
): InterviewScheduleResultDisplayCopy {
  const preview = parseInterviewScheduleResultPreviewFromMetadata(metadata);
  if (!preview) {
    return {
      message: 'Interview scheduling completed.',
      summary: '',
    };
  }
  return {
    message: preview.message,
    summary: preview.summary,
  };
}

export function interviewScheduleResultHasRows(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  return parseInterviewScheduleResultPreviewFromMetadata(metadata) != null;
}

export function normalizeInterviewScheduleResultAssistantMetadata(
  raw: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const preview = parseInterviewScheduleResultPreviewFromMetadata(raw);
  if (!preview) {
    return null;
  }
  const nested = raw['result'];
  const rowSource =
    nested != null && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : raw;
  return {
    ...raw,
    type: INTERVIEW_SCHEDULE_RESULT_META_TYPE,
    resultKind: INTERVIEW_SCHEDULE_RESULT_META_TYPE,
    message: preview.message,
    summary: preview.summary,
    responseMessage: preview.message,
    ats_message: preview.message,
    success_interview_schedules:
      rowSource['success_interview_schedules'] ?? raw['success_interview_schedules'],
    failed_interview_schedules:
      rowSource['failed_interview_schedules'] ?? raw['failed_interview_schedules'],
    skipped_interview_schedules:
      rowSource['skipped_interview_schedules'] ?? raw['skipped_interview_schedules'],
  };
}

export function interviewSchedulerRoleDisplayLabel(
  key: string | null | undefined,
): string {
  const k = (key ?? '').trim() as InterviewSchedulerRoleKey;
  if (!k) {
    return '';
  }
  return scheduledByLabelFromKey(k);
}
